// Navbar2.tsx
import {useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {NavbarAuthUser} from '@common/ui/navigation/navbar/navbar-auth-user';
import {useAuth} from '@common/auth/use-auth';
import {Link} from 'react-router-dom';
import {Trans} from '@common/i18n/trans';
import {useSettings} from '@common/core/settings/use-settings';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {LightbulbIcon} from '@common/icons/material/Lightbulb';
import {DarkModeIcon} from '@common/icons/material/DarkMode';
import './navbar.css';

/* ── Theme toggle ── */
function ThemeSwitcher() {
  const {themes} = useSettings();
  const {selectedTheme, selectTheme} = useThemeSelector();
  if (!selectedTheme || !themes?.user_change) return null;

  const isDark = selectedTheme.is_dark;
  return (
    <button
      type="button"
      className="lnb2-themeToggle"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      onClick={() => selectTheme(isDark ? 'light' : 'dark')}
    >
      {isDark ? <DarkModeIcon /> : <LightbulbIcon />}
    </button>
  );
}

/* ── Nav data ── */
type MegaCol = {title: string; items: {label: string; href: string; tag?: 'New'}[]};
type NavItem =
  | {type: 'link'; label: string; href: string; tag?: 'New'}
  | {type: 'mega'; label: string; cols: MegaCol[]};

const NAV: NavItem[] = [
  {
    type: 'mega',
    label: 'Products',
    cols: [
      {
        title: 'Build',
        items: [
          {label: 'AI Builder', href: '/'},
          {label: 'Templates', href: '/templates'},
          {label: 'Deployments', href: '/deployments'},
          {label: 'Databases', href: '/databases'},
        ],
      },
      {
        title: 'Publish',
        items: [
          {label: 'Custom Domains', href: '/domains'},
          {label: 'Analytics', href: '/analytics'},
          {label: 'Integrations', href: '/integrations', tag: 'New'},
        ],
      },
    ],
  },
  {
    type: 'mega',
    label: 'For Teams',
    cols: [
      {
        title: 'Use Cases',
        items: [
          {label: 'Startups', href: '/teams/startups'},
          {label: 'Enterprise', href: '/teams/enterprise'},
          {label: 'Agencies', href: '/teams/agencies'},
        ],
      },
      {
        title: 'Features',
        items: [
          {label: 'Collaboration', href: '/teams/collaboration'},
          {label: 'Admin Controls', href: '/teams/admin'},
          {label: 'SSO & Security', href: '/security'},
        ],
      },
    ],
  },
  {
    type: 'mega',
    label: 'Resources',
    cols: [
      {
        title: 'Learn',
        items: [
          {label: 'Documentation', href: '/docs'},
          {label: 'Guides', href: '/guides'},
          {label: 'Tutorials', href: '/tutorials'},
        ],
      },
      {
        title: 'Community',
        items: [
          {label: 'Blog', href: '/blog'},
          {label: 'Changelog', href: '/changelog', tag: 'New'},
          {label: 'Roadmap', href: '/roadmap'},
        ],
      },
    ],
  },
  {type: 'link', label: 'Security', href: '/security'},
  {type: 'link', label: 'Pricing', href: '/pricing'},
];

/* ── Chevron icon ── */
function Chevron() {
  return (
    <svg className="lnb2__chev" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Props ── */
interface Navbar2Props {
  topOffsetClass?: string;
  ctaHref?: string;
  showAuth?: boolean;
}

export function Navbar2({
  topOffsetClass = 'top-0',
  ctaHref = '/register',
  showAuth = true,
}: Navbar2Props) {
  const {isLoggedIn} = useAuth();

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 1);
    onScroll();
    window.addEventListener('scroll', onScroll, {passive: true});
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hover-intent for mega menus
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openWithDelay = (i: number) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setOpenIdx(i), 80);
  };
  const closeWithDelay = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenIdx(null), 140);
  };

  const items = useMemo(() => NAV, []);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    document.documentElement.style.overflow = sheetOpen ? 'hidden' : '';
    document.body.style.overflow = sheetOpen ? 'hidden' : '';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [sheetOpen]);

  return (
    <>
      <div className={clsx('lnb2-shell', topOffsetClass)}>
        <nav className={clsx('lnb2', scrolled && 'lnb2--pinned')}>

          {/* Logo */}
          <Link to="/" className="lnb2__logo" aria-label="Home">
            <Logo />
          </Link>

          {/* Desktop center nav */}
          <ul className="lnb2__center">
            {items.map((it, i) => {
              const isActive = openIdx === i && it.type === 'mega';

              if (it.type === 'link') {
                return (
                  <li key={i} className="lnb2__link">
                    <Link className="lnb2__linkBtn" to={it.href}>
                      {it.label}
                      {it.tag && <span className="lnb2-tag">{it.tag}</span>}
                    </Link>
                  </li>
                );
              }

              return (
                <li
                  key={i}
                  className={clsx('lnb2__link', isActive && 'is-active')}
                  onMouseEnter={() => openWithDelay(i)}
                  onMouseLeave={closeWithDelay}
                >
                  <button
                    type="button"
                    className="lnb2__linkBtn"
                    aria-haspopup="true"
                    aria-expanded={isActive}
                  >
                    {it.label}
                    <Chevron />
                  </button>

                  {isActive && (
                    <div
                      className="lnb2-mega"
                      role="menu"
                      onMouseEnter={() => openWithDelay(i)}
                      onMouseLeave={closeWithDelay}
                    >
                      {it.cols.map((col, c) => (
                        <div className="lnb2-mega__col" key={c}>
                          <div className="lnb2-mega__title">{col.title}</div>
                          <ul>
                            {col.items.map((li, k) => (
                              <li key={k}>
                                <Link to={li.href} className="lnb2-a" role="menuitem">
                                  {li.label}
                                  {li.tag && <span className="lnb2-tag">{li.tag}</span>}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Right */}
          <div className="lnb2__right">
            <ThemeSwitcher />
            <Link to="/contact" className="lnb2__login max-md:hidden">
              <Trans message="Contact sales" />
            </Link>
            {showAuth && (
              isLoggedIn
                ? <NavbarAuthUser />
                : <NavbarAuthButtons navbarColor="bg" />
            )}
            <button
              type="button"
              className="lnb2-burger"
              aria-label="Open menu"
              onClick={() => setSheetOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>

        </nav>
      </div>

      <MobileSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        items={items}
        ctaHref={ctaHref}
        showAuth={showAuth}
      />
    </>
  );
}

/* ── Mobile sheet ── */
function MobileSheet({
  open,
  onClose,
  items,
  ctaHref,
  showAuth,
}: {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  ctaHref: string;
  showAuth: boolean;
}) {
  const {isLoggedIn} = useAuth();

  return (
    <div className={clsx('lnb2-sheet', open && 'is-open')} aria-modal={open} role="dialog">
      {/* Top bar */}
      <div className="lnb2-sheet__bar">
        <Link to="/" className="lnb2__logo" onClick={onClose} aria-label="Home">
          <Logo />
        </Link>
        <div className="lnb2-sheet__icons">
          <ThemeSwitcher />
          <button type="button" className="lnb2-close" aria-label="Close menu" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Nav links */}
      <div className="lnb2-sheet__content" onClick={onClose}>
        {items.map((it, i) =>
          it.type === 'link' ? (
            <Link key={i} to={it.href} className="lnb2-sheet__link">
              {it.label}
            </Link>
          ) : (
            <details className="lnb2-acc" key={i}>
              <summary>
                {it.label}
                <Chevron />
              </summary>
              <div className="lnb2-acc__body">
                {it.cols.map((col, idx) => (
                  <div className="lnb2-acc__col" key={idx}>
                    <div className="lnb2-acc__title">{col.title}</div>
                    {col.items.map((li, k) => (
                      <Link key={k} to={li.href} className="lnb2-sheet__link">
                        {li.label}
                        {li.tag && <span className="lnb2-tag" style={{marginLeft: 8}}>{li.tag}</span>}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          ),
        )}

        {/* CTA */}
        <a href={ctaHref} className="lnb2__cta lnb2__cta--block" style={{marginTop: 24}}>
          <Trans message="Get started" />
        </a>

        {showAuth && !isLoggedIn && (
          <NavbarAuthButtons navbarColor="bg" />
        )}
        {showAuth && isLoggedIn && (
          <div style={{marginTop: 16}}>
            <NavbarAuthUser />
          </div>
        )}
      </div>
    </div>
  );
}
